"use client";
import { FirebaseApp, initializeApp } from "firebase/app";
import { Auth, getAuth } from "firebase/auth";
import { Firestore, getFirestore } from "firebase/firestore";
import { Analytics, getAnalytics } from "firebase/analytics";
import FirebaseAuthServiceClient from "@/app/lib/firebase/FirebaseAuth.client";
import { getMessaging } from "firebase/messaging";
import type { Messaging } from "firebase/messaging";
// import {getToken} from "firebase/messaging";
// import {sendFCMTokenToServer} from "@/app/services/users.service";
import { getStorage } from "firebase/storage";
import type { FirebaseStorage } from "firebase/storage";
import logger from "@/lib/logger";

/** Strip gs:// and trailing slashes so Storage URLs don't encode `%2F` into the bucket. */
export function normalizeFirebaseStorageBucket(
  raw?: string | null,
): string | undefined {
  if (!raw) return undefined;
  return raw
    .trim()
    .replace(/^gs:\/\//i, "")
    .replace(/\/+$/, "");
}

class FirebaseClientService {
  private static fbApp: FirebaseApp;
  private static fbAuth: Auth;
  private static fbAuthService: FirebaseAuthServiceClient;
  private static fbFirestore: Firestore;
  private static fbAnalytics: Analytics;
  private static fbMessaging: Messaging;
  private static fbStorage: FirebaseStorage;
  private static instance: FirebaseClientService;
  private static readonly firebaseConfig = {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
    storageBucket: normalizeFirebaseStorageBucket(
      process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    ),
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
    databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_ID,
  };

  public static async getFirebaseClient(): Promise<FirebaseClientService> {
    if (!FirebaseClientService.instance) {
      FirebaseClientService.instance = new FirebaseClientService();
      try {
        await FirebaseClientService.instance.initializeFirebase();
      } catch (error) {
        throw new Error(error);
      }
    }
    return FirebaseClientService.instance;
  }

  public getFirestoreClient(): Firestore {
    return FirebaseClientService.fbFirestore;
  }

  public getFirebaseAuth(): Auth {
    return FirebaseClientService.fbAuth;
  }

  public getFirebaseAuthService(): FirebaseAuthServiceClient {
    return FirebaseClientService.fbAuthService;
  }

  public getFirebaseStorage(): FirebaseStorage {
    if (!FirebaseClientService.fbStorage) {
      FirebaseClientService.fbStorage = getStorage(FirebaseClientService.fbApp);
    }
    return FirebaseClientService.fbStorage;
  }

  public getFirebaseAnalytics(): Analytics | null {
    // Conditionally initialize Analytics only if window is defined (client-side)
    if (typeof window !== "undefined") {
      FirebaseClientService.fbAnalytics = getAnalytics(
        FirebaseClientService.fbApp,
      );

      return FirebaseClientService.fbAnalytics;
    } else {
      return null;
    }
  }

  public getFirebaseMessaging(): Messaging | null {
    // Conditionally initialize Analytics only if window is defined (client-side)
    if (typeof window !== "undefined") {
      FirebaseClientService.fbMessaging = getMessaging(
        FirebaseClientService.fbApp,
      );
      return FirebaseClientService.fbMessaging;
    } else {
      return null;
    }
  }

  /*
        public async getFCMTokenAndStore(email_id: string): Promise<string> {
            getToken(FirebaseClientService.fbMessaging, {vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY}).then(async (currentToken) => {
                if (currentToken) {
                    // console.log('FCM token:', currentToken);
                    logger.info('FCM token:', { token: currentToken });
                    // Send the token to the server
                    const response = await sendFCMTokenToServer(email_id, currentToken);
                    // console.log('Response from server:', response);
                    logger.info('Response from server:', { response });
                    return currentToken;
                } else {
                    // console.log('No registration token available. Request permission to generate one.');
                    logger.info('No registration token available. Request permission to generate one.');
                }
            }).catch((error) => {
                // console.error('Error getting FCM token:', error);
                logger.error('Error getting FCM token:', { error: String(error) });
            });
            return "";
        }
    */

  private async initializeFirebase(): Promise<void> {
    try {
      if (!process.env.NEXT_PUBLIC_ENV) {
        throw new Error("Environment variable NEXT_PUBLIC_ENV not set");
      }
      FirebaseClientService.fbApp = initializeApp(
        FirebaseClientService.firebaseConfig,
      );
      FirebaseClientService.fbFirestore = getFirestore(
        FirebaseClientService.fbApp,
      );
      FirebaseClientService.fbAuth = getAuth(FirebaseClientService.fbApp);
      FirebaseClientService.fbAuthService =
        await FirebaseAuthServiceClient.getInstance();
      // Conditionally initialize Analytics only if window is defined (client-side)
      if (typeof window !== "undefined") {
        FirebaseClientService.fbAnalytics = getAnalytics(
          FirebaseClientService.fbApp,
        );
      }

      if (process.env.NEXT_PUBLIC_ENV === "development") {
        // setLogLevel('debug');
      }
    } catch (e) {
      logger.error("Error initializing Firebase:", { error: String(e) });
    } finally {
    }
  }
}

export default FirebaseClientService;
